import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'

interface Props {
  daysUntilExpiry?: number | null
}

export default function NoticeBanner({ daysUntilExpiry }: Props) {
  const [dismissed, setDismissed] = useState(false)

  // Check localStorage for dismissal (10 days)
  useEffect(() => {
    if (daysUntilExpiry == null) return
    const dismissKey = `subscription_notice_dismissed_${daysUntilExpiry}`
    const dismissedUntil = localStorage.getItem(dismissKey)
    if (dismissedUntil) {
      const dismissDate = new Date(dismissedUntil)
      if (dismissDate > new Date()) {
        setDismissed(true)
      } else {
        localStorage.removeItem(dismissKey)
      }
    }
  }, [daysUntilExpiry])

  const handleDismiss = () => {
    if (daysUntilExpiry == null) return
    setDismissed(true)
    // Dismiss for 10 days
    const dismissUntil = new Date()
    dismissUntil.setDate(dismissUntil.getDate() + 10)
    localStorage.setItem(`subscription_notice_dismissed_${daysUntilExpiry}`, dismissUntil.toISOString())
  }

  if (daysUntilExpiry == null) return null
  if (daysUntilExpiry > 90) return null
  if (dismissed) return null

  // Calculate expiry date
  const expiryDate = new Date()
  expiryDate.setDate(expiryDate.getDate() + daysUntilExpiry)

  return (
    <motion.div
      className="mt-6 p-4 rounded-xl border border-yellow-200 bg-yellow-50 text-yellow-800"
      initial={{ y: 8, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ type: 'spring', stiffness: 260, damping: 22 }}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <p className="font-semibold mb-1">
            Your subscription expires on {expiryDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}.
          </p>
          <p className="text-sm">
            Please go to your{' '}
            <Link to="/settings" className="underline font-medium hover:text-yellow-900">
              Settings & Details
            </Link>
            {' '}to view your billing and subscription renewal options.
          </p>
        </div>
        <button
          onClick={handleDismiss}
          className="text-sm underline hover:text-yellow-900 focus:outline-none focus:ring-2 focus:ring-yellow-400/60 rounded px-2"
          title="Dismiss for 10 days"
        >
          Dismiss
        </button>
      </div>
    </motion.div>
  )
}



